import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { CalendarClock, ArrowRight, Phone, Mail } from 'lucide-react';

const dayLabel = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(d);
  target.setHours(0, 0, 0, 0);
  const diff = Math.round((target - today) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
};

/**
 * Dashboard widget: call bookings made via the website for today + the next 7
 * days, so the team can see upcoming customer calls at a glance.
 */
export const UpcomingBookingsWidget = () => {
  const [data, setData] = useState({ items: [], total: 0 });
  const navigate = useNavigate();

  const load = () => {
    api.get('/website-form/bookings')
      .then((r) => setData(r.data || { items: [], total: 0 }))
      .catch(() => {});
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  if (!data.items?.length) return null;

  return (
    <Card className="border-sky-300 bg-sky-50/60" data-testid="upcoming-bookings-widget">
      <CardContent className="p-5">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-sky-100 flex items-center justify-center flex-shrink-0">
            <CalendarClock className="w-5 h-5 text-sky-600" />
          </div>
          <div>
            <p className="font-semibold text-slate-900">
              {data.total} upcoming call{data.total === 1 ? '' : 's'} booked
            </p>
            <p className="text-sm text-slate-600 mt-0.5">
              Calls booked via the website for today and the next 7 days.
            </p>
          </div>
        </div>

        <ul className="space-y-2">
          {data.items.slice(0, 8).map((it) => (
            <li
              key={it.id}
              className="flex items-center justify-between gap-3 bg-white border border-sky-100 rounded-lg px-3 py-2"
              data-testid={`booking-item-${it.id}`}
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">
                  {it.name}
                </p>
                <p className="text-xs text-slate-500 truncate flex items-center gap-3 mt-0.5">
                  {it.phone && (
                    <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{it.phone}</span>
                  )}
                  {it.email && (
                    <span className="inline-flex items-center gap-1 truncate"><Mail className="w-3 h-3" />{it.email}</span>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Badge className="text-[10px] bg-sky-100 text-sky-700 border-sky-200" data-testid={`booking-when-${it.id}`}>
                  {dayLabel(it.booking_at)}{it.time ? ` · ${it.time}` : ''}
                </Badge>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 border-sky-300 text-sky-700 hover:bg-sky-50"
                  onClick={() => navigate(it.converted && it.customer_id ? `/customers/${it.customer_id}` : '/website-leads')}
                  data-testid={`booking-open-btn-${it.id}`}
                >
                  Open <ArrowRight className="w-3.5 h-3.5 ml-1" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};
